import { useState } from "react";

import { Link } from "react-router";

import { AlertBell } from "@/components/shell/AlertBell";
import { NavItem } from "@/components/shell/NavItem";
import { DropdownMenu } from "@/components/ui/DropdownMenu";
import { Popover } from "@/components/ui/Popover";
import { LogOutIcon, MenuIcon, MoonIcon, SearchIcon, SettingsIcon, SunIcon } from "@/components/ui/icons";
import { useCurrentActor } from "@/lib/current-actor";
import { MORE_SHEET_DESTINATIONS, PHONE_BAR_DESTINATIONS } from "@/lib/destinations";
import { useDocumentTitle } from "@/lib/use-document-title";
import { useSignOut } from "@/lib/use-sign-out";
import { useThemePreference } from "@/lib/use-theme-preference";

/**
 * Story 17.1 — the desktop floating nav bar, replacing both `NavRail.tsx`
 * (deleted) and this file's own former shape (a full-width header sitting
 * above the rail). Owner-requested (2026-09-10/11), inspired by runpod.io's
 * own floating pill nav: one sticky, pill-shaped bar at the top of the
 * content area holding the wordmark, the four `phoneSlot: true`
 * destinations as direct items, a "Menu" item (Story 20.1 relabeled "More")
 * for the rest, and the search/alerts/account cluster this component
 * already owned.
 *
 * `PHONE_BAR_DESTINATIONS`/`MORE_SHEET_DESTINATIONS` (`lib/destinations.ts`)
 * are reused directly, not re-derived — the phone bottom bar and its "Menu"
 * sheet already encode the exact frequent/rest split this bar wants
 * (epic-17-floating-nav.md's own Decisions section, Owner-approved
 * 2026-09-11): desktop and phone deliberately share one frequent set.
 *
 * The "Menu" panel reuses `NavItem`'s own `"sheet"` layout, the same one
 * `MoreSheet.tsx` renders its own remaining items with — real active-state
 * highlighting and icon rendering, not a hand-rolled duplicate — inside a
 * `Popover` (`components/ui/Popover.tsx`, the same Radix primitive
 * `SavedViewChips.tsx`'s "Save this view" flow already proved out), not a
 * new hand-built overlay.
 *
 * Story 17.1's gear-icon `SETTINGS_MENU_ITEMS` dropdown (the seven
 * `Settings.tsx` sub-pages) is still gone — that reasoning is unchanged.
 * Story 20.1 adds back a single, different gear-icon control: a plain link
 * straight to `/settings` itself (the destination's own index page, not its
 * seven sub-pages), in the same icon-only utility cluster as search/
 * `AlertBell`/account, alongside `settings` gaining `hidden: true` in
 * `destinations.ts` so the Menu panel stops listing it — a deliberate,
 * Owner-approved reversal of the "no second path to Settings" reasoning
 * above (Owner was told that history directly and asked for it back
 * anyway, 2026-09-14), not the same oversight repeating.
 *
 * The greeting/page-title two-line block this file used to show on its
 * left is gone too, except the page title survives (still `useDocumentTitle`
 * — `RecordPage.tsx`'s own header and every list route's own `<h1>` stay
 * `md:hidden`, deliberately relying on this bar to carry the title on
 * desktop, so dropping it outright would leave every desktop screen titleless).
 * It now renders as a single truncating line between the nav pill and the
 * utility cluster rather than its own two-line column — there simply isn't
 * room for both a greeting and five real nav items in one pill-shaped bar,
 * and the greeting was the lower-value of the two to keep.
 *
 * **Responsive collapse, found live in Story 17.1's own Tier B review
 * (2026-09): the bar's min-content width (measured 1189px) overflowed its
 * own container below a real ~1255px viewport**, clipping the alert bell
 * and the account/sign-out menu off-screen entirely — a real desktop width
 * (a 1024px or 1280px-split-screen window), not an edge case, and the one
 * control that let a signed-in visitor sign out at all on desktop. Two
 * things now give way, in this order, before either nav labels or the
 * utility cluster ever would: the wordmark hides below `xl` (1280px —
 * `lg`'s own 1024px threshold is inclusive of exactly 1024px itself, which
 * is one of the three widths this bar must fit at, so the collapse has to
 * kick in a step earlier than that) (branding, not navigation — the least
 * load-bearing element in the bar), and the search trigger collapses to an
 * icon-only 44px box below that same `xl` breakpoint (mirroring
 * `PhoneTopBar.tsx`'s own already-icon-only search button exactly, not a
 * new shape) rather than its full placeholder-text-plus-`⌘K` form.
 * `NavItem`'s own `shortLabel` resolution for `"floating"` (its own doc
 * comment) does the rest — real, still-readable labels, just shorter ones,
 * never icon-only navigation.
 *
 * Sticky + `overscroll-behavior: contain` (VC-17.1-06/07): this bar itself
 * only needs `sticky top-0` (below, a real Tailwind utility, not a bare
 * value the "no hardcoded values" guard would flag) — the `overscroll-contain`
 * half of the fix from the mockup's own scroll-containment bug lives on
 * `AppShell.tsx`'s content column, the actual scrolling container this bar
 * sticks inside, not on the bar itself.
 */
export function GlobalTopBar({ onOpenSearch }: { onOpenSearch: () => void }) {
  const { data: actor } = useCurrentActor();
  const { isSigningOut, signOut } = useSignOut();
  const { isDark, toggle: toggleTheme } = useThemePreference();
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const pageTitle = useDocumentTitle();

  function initials(displayName: string): string {
    const parts = displayName.trim().split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] ?? "";
    const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
    return (first + last).toUpperCase();
  }

  return (
    <header
      data-shell-scope="global-top-bar"
      className="sticky top-0 z-10 mx-4 hidden h-global-topbar shrink-0 items-center gap-4 rounded-full border border-border-hairline bg-surface px-4 shadow-card md:mx-8 md:flex"
    >
      {/* Story 19.1: `text-heading` here is a size reuse for the brand
          wordmark, not a real heading — `font-sans` opts it back out of the
          `.text-heading` role's new Fraunces rule in index.css. */}
      <span className="hidden shrink-0 text-heading font-sans font-semibold text-ink xl:inline-block">
        PropDesk
      </span>

      <nav aria-label="Primary" className="flex shrink-0 items-center gap-1">
        <ul className="flex items-center gap-1">
          {PHONE_BAR_DESTINATIONS.map((destination) => (
            <li key={destination.id}>
              <NavItem destination={destination} layout="floating" />
            </li>
          ))}
        </ul>
        <Popover
          open={moreOpen}
          onOpenChange={setMoreOpen}
          align="start"
          trigger={
            <button
              type="button"
              aria-label="Menu"
              className="flex h-tap-target-min items-center gap-2 rounded-md px-3 text-body text-ink-secondary hover:bg-surface-hover"
            >
              <MenuIcon className="shrink-0" />
              <span>Menu</span>
            </button>
          }
        >
          <nav aria-label="Menu">
            <ul className="flex flex-col gap-1">
              {MORE_SHEET_DESTINATIONS.map((destination) => (
                <li key={destination.id}>
                  <NavItem destination={destination} layout="sheet" onNavigate={() => setMoreOpen(false)} />
                </li>
              ))}
            </ul>
          </nav>
        </Popover>
      </nav>

      <p className="min-w-0 flex-1 truncate text-label font-medium text-ink-secondary">{pageTitle}</p>

      <button
        type="button"
        onClick={onOpenSearch}
        aria-label="Search"
        className="flex h-tap-target-min w-tap-target-min shrink-0 items-center justify-center rounded-full text-ink-secondary hover:bg-surface-hover xl:w-full xl:max-w-xs xl:shrink xl:justify-start xl:gap-2 xl:rounded-full xl:bg-surface-sunken xl:px-4"
      >
        <SearchIcon className="shrink-0" />
        <span className="hidden flex-1 truncate text-left text-body xl:inline">
          Search tenants, properties, or ask a question…
        </span>
        <kbd
          aria-hidden="true"
          className="hidden shrink-0 rounded-sm bg-surface px-1.5 py-0.5 font-sans text-meta font-medium text-ink-tertiary xl:inline-block"
        >
          ⌘K
        </kbd>
      </button>

      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={() => void toggleTheme()}
          aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
          className="flex h-tap-target-min w-tap-target-min items-center justify-center rounded-full text-ink-secondary hover:bg-surface-hover"
        >
          {isDark ? <MoonIcon /> : <SunIcon />}
        </button>
        <Link
          to="/settings"
          aria-label="Settings"
          className="flex h-tap-target-min w-tap-target-min items-center justify-center rounded-full text-ink-secondary hover:bg-surface-hover"
        >
          <SettingsIcon />
        </Link>
        <AlertBell />
        {actor ? (
          <DropdownMenu
            open={accountMenuOpen}
            onOpenChange={setAccountMenuOpen}
            align="end"
            trigger={
              <button
                type="button"
                aria-label={`Account: ${actor.display_name}`}
                className="flex h-tap-target-min w-tap-target-min items-center justify-center rounded-full bg-accent text-label font-semibold text-on-accent hover:bg-accent-hover"
              >
                {initials(actor.display_name)}
              </button>
            }
            items={[
              {
                label: isSigningOut ? "Signing out…" : "Sign out",
                icon: <LogOutIcon />,
                onSelect: () => void signOut(),
                disabled: isSigningOut,
              },
            ]}
          />
        ) : null}
      </div>
    </header>
  );
}
